import { ApostleFilterInput, ApostleLite } from '@apeiron/apostle'
import { MayNull, NextImage, noForwardProps } from '@apeiron/temp'
import { Box, ButtonBase, styled, useMediaQuery } from '@mui/material'
import CrossPNG from '@public/icons/icon-remove.png'
import ApostleCard from '@src/components/apostles/card/ApostleCard'
import MarketplaceApostleCard from '@src/components/apostles/card/MarketplaceApostleCard'
import ApostleSelectionPopup from '@src/components/apostles/share/multi-select/ApostleSelectionPopup'
import EmptyApostleSlot from '@src/components/apostles/share/multi-select/EmptyApostleSlot'
import { ApostleSkillsProvider } from '@src/contexts/apostle/ApostleSkillsContext'
import { GetMyApostlesRequestInput } from '@src/hooks/graphql/useGetMyApostles'
import theme from '@src/styles/theme'
import { ReactNode, useCallback, useState } from 'react'

const SlotsContainer = styled(Box, noForwardProps)<{ __grid: boolean }>`
  display: flex;
  gap: 24px;
  justify-content: center;
  flex-wrap: wrap;

  ${props => props.theme.breakpoints.down('res768')} {
    ${props =>
      props.__grid &&
      `
        display: grid;
        grid-template-columns: repeat(2, 1fr);
    `}

    width: 250px;
    margin: 0 auto;
    row-gap: 24px;
  }
`

const SelectionSlot = styled(ButtonBase)`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
`

const CrossButton = styled(ButtonBase)`
  position: absolute;
  width: 25%;
  ${props => props.theme.breakpoints.up('res1024')} {
    top: -5%;
    right: -7%;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    top: -5%;
    right: -7%;
  }

  :focus {
    filter: brightness(1.2);
  }
`

const CrossImage = styled(NextImage)`
  aspect-ratio: 1;
  width: 100%;
`

const ApostleMultiSelectionSlots = ({
  className,
  selectedApostles,
  slotCount,
  setSelectedApostles,
  defaultFilterInput,
  renderPopupCard,
  emptySlot,
  fixedApostles,
  getStatusText = () => '',
  showCloseButton = true,
  overridePopupRequestParams
}: Props) => {
  const [isPopupOpen, setIsPopupOpen] = useState(false)

  const isMobile = useMediaQuery(theme.breakpoints.down('res768'))

  const handleOpenSelectPopup = useCallback(() => {
    setIsPopupOpen(true)
  }, [])

  const handleCloseSelectPopup = useCallback(() => {
    setIsPopupOpen(false)
  }, [])

  const handleConfirmSelect = useCallback(
    (apostles: ApostleLite[]) => {
      setSelectedApostles(apostles)
    },
    [setSelectedApostles]
  )

  const handleRemoveApostle = useCallback(
    (apostle: ApostleLite) => {
      setSelectedApostles(selectedApostles.filter(a => a.id !== apostle.id))
    },
    [selectedApostles, setSelectedApostles]
  )

  const renderSlots = () => {
    const slots = Array.from({ length: slotCount })

    return slots.map((_, index) => {
      if (fixedApostles && fixedApostles[index]) {
        const fixedApostle = fixedApostles[index]

        return (
          <SelectionSlot
            disabled
            key={`fixed-${fixedApostle.id}`}
            className='Selection-Slot'
          >
            <MarketplaceApostleCard
              apostle={fixedApostle}
              disabled
              statusText={getStatusText(fixedApostle)}
            />
          </SelectionSlot>
        )
      }

      const fixedApostlesCount = fixedApostles?.length || 0

      const selectedApostle = selectedApostles[index - fixedApostlesCount]

      if (selectedApostle) {
        return (
          <SelectionSlot
            key={`selected-${selectedApostle.id}`}
            className='Selection-Slot'
          >
            <ApostleCard
              smallCard={isMobile}
              onClick={handleOpenSelectPopup}
              apostle={selectedApostle}
            />
            {showCloseButton && (
              <CrossButton onClick={() => handleRemoveApostle(selectedApostle)}>
                <CrossImage src={CrossPNG} />
              </CrossButton>
            )}
          </SelectionSlot>
        )
      }

      const key = `empty-slot-${index}`

      return (
        <SelectionSlot
          key={key}
          className='Selection-Slot'
          onClick={handleOpenSelectPopup}
        >
          {emptySlot || <EmptyApostleSlot />}
        </SelectionSlot>
      )
    })
  }

  const handleRenderPopupCard = useCallback(
    ({ apostle, isSelected, onClick }: RenderPopupCardProps) => {
      return renderPopupCard({ apostle, isSelected, onClick })
    },
    [renderPopupCard]
  )

  return (
    <ApostleSkillsProvider>
      <Box className={className}>
        <SlotsContainer __grid={slotCount > 1}>{renderSlots()}</SlotsContainer>
        <ApostleSelectionPopup
          selectLimit={slotCount - (fixedApostles?.length || 0)}
          selectedApostles={selectedApostles}
          onConfirm={handleConfirmSelect}
          onClose={handleCloseSelectPopup}
          isOpen={isPopupOpen}
          defaultFilterInput={defaultFilterInput}
          renderCard={handleRenderPopupCard}
          overrideRequestParameters={overridePopupRequestParams}
        />
      </Box>
    </ApostleSkillsProvider>
  )
}

export type RenderPopupCardProps = {
  apostle: ApostleLite
  isSelected: boolean
  onClick?: (apostle: MayNull<ApostleLite>) => void
  selectingApostles?: ApostleLite[]
}

type Props = {
  className?: string
  slotCount: number
  setSelectedApostles: (apostles: ApostleLite[]) => void
  selectedApostles: ApostleLite[]
  defaultFilterInput?: ApostleFilterInput
  renderPopupCard: (props: RenderPopupCardProps) => ReactNode
  emptySlot?: ReactNode
  fixedApostles?: ApostleLite[]
  showCloseButton?: boolean
  getStatusText?: (apostle: ApostleLite) => string
  overridePopupRequestParams?: (
    requestInput: GetMyApostlesRequestInput
  ) => GetMyApostlesRequestInput
}

export default ApostleMultiSelectionSlots
